import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon, ClipboardListIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { formatPurchaseOrderApiError } from "../api/purchase-orders-client";
import { useCreatePurchaseOrder } from "../api/purchase-orders-queries";
import { useSuppliersLookupQuery } from "../api/suppliers-queries";
import { PoFormDateField } from "../ui/purchase/PoFormDateField";
import { PurchaseOrderLineEditor } from "../ui/purchase/PurchaseOrderLineEditor";
import { PurchaseOrderSuggestedLinesCard } from "../ui/purchase/PurchaseOrderSuggestedLinesCard";
import {
  mergeSuggestedIntoDraftLines,
  newDraftLine,
  todayDateString,
  toLinePayload,
  type PoDraftLineRow,
} from "../ui/purchase/poDraftUtils";
import { inferPoSupplierFromLines } from "../ui/purchase/inferPoSupplierFromLines";
import { PO_MENU_TITLE } from "../ui/purchase/purchaseLaoLabels";

export function PurchaseOrderCreatePage() {
  const nav = useNavigate({ from: "/app/inventory/purchase-orders/create" });
  const createPo = useCreatePurchaseOrder();
  const suppliers = useSuppliersLookupQuery();

  const [supplierId, setSupplierId] = useState("");
  const [orderDate, setOrderDate] = useState(todayDateString());
  const [expectedDate, setExpectedDate] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState<PoDraftLineRow[]>([newDraftLine()]);
  const supplierTouchedRef = useRef(false);

  useEffect(() => {
    if (supplierId || supplierTouchedRef.current) return;
    let cancelled = false;
    void inferPoSupplierFromLines(lines).then((id) => {
      if (!cancelled && id) setSupplierId(id);
    });
    return () => {
      cancelled = true;
    };
  }, [lines, supplierId]);

  const existingItemKeys = useMemo(() => {
    const set = new Set<string>();
    for (const l of lines) {
      if (l.itemId) set.add(`${l.itemType}:${l.itemId}`);
    }
    return set;
  }, [lines]);

  const supplierOptions = useMemo(
    () =>
      (suppliers.data ?? []).map((s) => ({
        value: s.id,
        label: s.code ? `${s.name} (${s.code})` : s.name,
      })),
    [suppliers.data],
  );

  const validate = (): string | null => {
    if (!supplierId) return "ຕ້ອງເລືອກຜູ້ສະໜອງ";
    if (!orderDate) return "ຕ້ອງໃສ່ວັນທີສັ່ງ";
    if (lines.length === 0) return "ຕ້ອງມີຢ່າງໜ້ອຍ 1 ບັນທັດ";
    for (const line of lines) {
      if (!line.itemId) return "ທຸກບັນທັດຕ້ອງເລືອກລາຍການ";
      if (line.qtyOrdered <= 0) return "ປະລິມານຕ້ອງໃຫຍ່ກວ່າ 0";
    }
    return null;
  };

  const onSave = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    try {
      const item = await createPo.mutateAsync({
        supplierId,
        orderDate,
        expectedDate: expectedDate.trim() ? expectedDate : null,
        note: note.trim() ? note : null,
        lines: toLinePayload(lines),
      });
      toast.success("ສ້າງ PO ສຳເລັດ");
      nav({
        to: "/app/inventory/purchase-orders/$id",
        params: { id: item.id },
      });
    } catch (e) {
      toast.error(formatPurchaseOrderApiError(e));
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: PO_MENU_TITLE, to: "/app/inventory/purchase-orders" },
            { label: "ສ້າງ PO" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ສ້າງໃບສັ່ງຊື້</h2>
            <p className="text-muted-foreground">ຮ່າງ (DRAFT) · Hub ກຳນົດອັດຕະໂນມັດ</p>
          </div>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/inventory/purchase-orders" })}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ClipboardListIcon className="size-5" />
                ຫົວ PO
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <span className="font-medium text-sm">ຜູ້ສະໜອງ *</span>
                <SimpleSelect
                  placeholder="ເລືອກຜູ້ສະໜອງ"
                  value={supplierId || undefined}
                  onValueChange={(v) => {
                    supplierTouchedRef.current = true;
                    setSupplierId(v);
                  }}
                  options={supplierOptions}
                />
              </div>
              <PoFormDateField
                label="ວັນທີສັ່ງ"
                value={orderDate}
                onChange={setOrderDate}
                placeholder="ວັນທີສັ່ງ"
                requiredMark
              />
              <PoFormDateField
                label="ຄາດວ່າຈະຮັບ"
                value={expectedDate}
                onChange={setExpectedDate}
                placeholder="ທາງເລືອກ"
              />
              <div className="space-y-2 sm:col-span-2">
                <span className="font-medium text-sm">ໝາຍເຫດ</span>
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="ທາງເລືອກ"
                />
              </div>
            </CardContent>
          </Card>

          <PurchaseOrderSuggestedLinesCard
            supplierId={supplierId}
            existingItemKeys={existingItemKeys}
            onAdd={(picks) =>
              setLines((prev) => mergeSuggestedIntoDraftLines(prev, picks))
            }
          />

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">ບັນທັດສັ່ງຊື້</CardTitle>
            </CardHeader>
            <CardContent>
              <PurchaseOrderLineEditor lines={lines} onChange={setLines} />
            </CardContent>
          </Card>
        </div>

        <div className="mt-6 flex justify-end gap-2 border-t pt-6">
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/inventory/purchase-orders" })}
          >
            ຍົກເລີກ
          </Button>
          <Button onClick={onSave} isLoading={createPo.isPending}>
            ບັນທຶກຮ່າງ
          </Button>
        </div>
      </Main>
    </>
  );
}
